import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ButtonBase
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Edit, Visibility, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../services/axios';
import { debounce } from 'lodash';

function Cliente() {
  const navigate = useNavigate();

  // Estados dos filtros
  const [ativo, setAtivo] = useState(true);
  const [codMatriz, setCodMatriz] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  const [tipo, setTipo] = useState('TODOS'); // dropdown para o tipo
  const [loading, setLoading] = useState(false);

  // Estados para listagem/paginação
  const [clientes, setClientes] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const registrosPorPagina = 10;
  const API_URL = '/api/ComClientes';

  // Função que chama o backend com os filtros
  const localizarClientes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          q: termoBusca,
          ativo,
          page: pagina - 1, // zero-based
          size: registrosPorPagina,
          codMatriz,
          municipio,
          opcaoSelecao: tipo
        }
      });
      if (data.content && data.totalPages !== undefined) {
        setClientes(data.content);
        setTotalPaginas(data.totalPages);
      } else {
        setClientes(data);
        setTotalPaginas(1);
      }
    } catch (error) {
      console.error('Erro ao localizar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para carregar a lista padrão
  const carregarClientes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}?page=${pagina - 1}&size=${registrosPorPagina}&municipio=${municipio}`
      );
      if (data.content && data.totalPages !== undefined) {
        setClientes(data.content);
        setTotalPaginas(data.totalPages);
      } else {
        setClientes(data);
        setTotalPaginas(1);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debouncedBuscar = useCallback(
    debounce(() => {
      localizarClientes();
    }, 500),
    [ativo, codMatriz, municipio, termoBusca, tipo, pagina]
  );

  // Dispara busca automática ao alterar filtros ou paginação
  useEffect(() => {
    debouncedBuscar();
    return debouncedBuscar.cancel;
  }, [ativo, codMatriz, municipio, termoBusca, tipo, pagina, debouncedBuscar]);

  // Limpa filtros e recarrega a lista padrão
  const limparFiltros = () => {
    setAtivo(true);
    setCodMatriz('');
    setMunicipio('');
    setTermoBusca('');
    setTipo('TODOS');
    setPagina(1);
    carregarClientes();
  };

  // Ações de navegação e edição
  const handleNovo = () => navigate('/cliente/edit');
  const handleEditar = (idCliente) => navigate(`/cliente/edit/${idCliente}`);
  const handleExcluir = (idCliente) => {
    console.log('Excluir cliente ID:', idCliente);
    // Exemplo de exclusão:
    // await axios.delete(`${API_URL}/${idCliente}`);
    // localizarClientes();
  };

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop de carregamento */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Cabeçalho */}
      <Typography variant="h4" align="center" gutterBottom>
        Listagem de Pessoa: Cliente
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ativo}
                    onChange={(e) => setAtivo(e.target.checked)}
                  />
                }
                label="Ativo?"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Cod. Matriz"
                variant="outlined"
                size="small"
                fullWidth
                value={codMatriz}
                onChange={(e) => setCodMatriz(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Município"
                variant="outlined"
                size="small"
                fullWidth
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Buscar por Nome, Nome Fantasia, CPF/CNPJ ou Código"
                variant="outlined"
                size="small"
                fullWidth
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={tipo}
                  label="Tipo"
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <MenuItem value="ASSINADO">ASSINADO</MenuItem>
                  <MenuItem value="MENSALISTA">MENSALISTA</MenuItem>
                  <MenuItem value="VENDEU">VENDEU</MenuItem>
                  <MenuItem value="COMPREVEU">COMPREVEU</MenuItem>
                  <MenuItem value="ETIQUETADO">ETIQUETADO</MenuItem>
                  <MenuItem value="TODOS">TODOS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="contained" onClick={limparFiltros} fullWidth>
                Limpar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Card de Resultados (Tabela e Ações) */}
      <Card>
        <CardContent>
          {/* Área de Ações Rápidas */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" onClick={()=>  navigate(`/clientes/edit`) }>
              + Novo
            </Button>
          </Box>

          {/* Instrução de Busca em uma única linha */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2">
              Buscar por Nome, Nome Fantasia, CPF/CNPJ ou Código (digite somente
              números para CPF/CNPJ)
            </Typography>
          </Box>

          {/* Tabela de Resultados */}
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Código</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Nome / Fantasia</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Tipo</strong>
                  </TableCell>
                  <TableCell>
                    <strong>CPF/CNPJ</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Cidade / Estado</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Ações</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum cliente encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  clientes.map((cliente, index) => (
                    <TableRow key={index}>
                      <TableCell>{cliente.id || cliente.codigo}</TableCell>
                      <TableCell>{cliente.nome}</TableCell>
                      <TableCell>
                        {cliente.fisica ? 'Física' : 'Jurídica'}
                      </TableCell>
                      <TableCell>{cliente.documento}</TableCell>
                      <TableCell>
                        {cliente.municipioNome} - {cliente.estadoSigla}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          {/* Botão Editar: ícone e texto clicáveis */}
                          <ButtonBase
                            onClick={() => navigate(`/clientes/edit/${cliente.id}`)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row', // força que seja sempre linha
                              justifyContent: 'center',
                              alignItems: 'center',
                              p: 1,
                            }}
                          >
                            <Edit fontSize="small" sx={{ color: 'orange' }} /> <br />
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                              EDITAR
                            </Typography>
                          </ButtonBase>

                          {/* Botão Consultar */}
                          <ButtonBase
                            onClick={() => navigate(`/clientes/edit/${cliente.id}`)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              p: 1,
                            }}
                          >
                            <Visibility fontSize="small" sx={{ color: 'blue' }} />
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                              CONSULTAR
                            </Typography>
                          </ButtonBase>

                          {/* Botão Deletar */}
                          <ButtonBase
                            onClick={() =>alert(cliente.id)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              p: 1,
                            }}
                          >
                            <Cancel fontSize="small" sx={{ color: 'red' }} />
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                              DELETAR
                            </Typography>
                          </ButtonBase>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2">
              {`1-${Math.min(
                registrosPorPagina,
                clientes.length
              )} de ${totalPaginas * registrosPorPagina}`}
            </Typography>
            <Pagination
              count={totalPaginas}
              page={pagina}
              onChange={handleChangePagina}
              color="primary"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Cliente;
